import * as React from 'react';

import { CommentsRail } from './comments-rail';
import { CommentsRailLayout } from './comments-rail-layout';
import { useCommentsRail } from './comments-rail-provider';

/**
 * Renders the page's content with the comments rail docked beside it.
 *
 * Placed inside the page's shell, below the bar, so the rail is a column of the page
 * rather than of the window — the same relationship the artifact panel has to the bar
 * above it.
 *
 * The layout is rendered whether or not anything has offered itself as a comment
 * target, and that is load-bearing: swapping between a bare `children` and a panel
 * group gives the subtree a different parent, which unmounts the editor inside it.
 * That editor is what publishes the target, so its teardown withdrew the target,
 * which swapped the tree back, which mounted a new editor — a loop that reloads the
 * page for as long as it is left running. The tree shape stays put; only the rail's
 * own column comes and goes.
 */
export const CommentsRailOutlet: React.FC<{
  /**
   * False where this outlet is not the one that should host the column.
   *
   * A page can have more than one — the chat has an outlet around its split, and the
   * resource inside the panel has one below its own bar — and which of them shows the
   * rail depends on the view. They are all mounted all the time and stand down
   * instead of unmounting: swapping which outlet exists would give the editor a
   * different parent, tearing down the artifact panel and everything it is holding.
   */
  enabled?: boolean;
  /** Passed through to the rail's column — see `CommentsRailLayout`. */
  railClassName?: string;
  children: React.ReactNode;
}> = ({ enabled = true, railClassName, children }) => {
  const rail = useCommentsRail();

  // No provider above us at all. Static for the life of the tree, so branching on it
  // cannot remount anything.
  if (rail === null) {
    return <>{children}</>;
  }

  const { target } = rail;

  return (
    <CommentsRailLayout
      open={rail.open && target !== null}
      onOpenChange={rail.setOpen}
      {...(railClassName !== undefined && { railClassName })}
      rail={
        target === null || !enabled ? null : (
          <CommentsRail
            editor={target.editor}
            author={target.author}
            openThreadId={rail.openThreadId}
            onOpenThread={rail.setOpenThreadId}
            onClose={() => rail.setOpen(false)}
          />
        )
      }
    >
      {children}
    </CommentsRailLayout>
  );
};
